import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/utils/constants";

const COLUMNS = [
  { title: "Product", links: [
    { label: "Features", href: "/#features" },
    { label: "Pricing", href: "/#pricing" },
    { label: "Roadmap", href: "/#roadmap" },
    { label: "Blog", href: "/blog" },
    { label: "Templates", href: "/templates" },
    { label: "Get the App", href: "/#hero" },
  ]},
  { title: "Compare", links: [
    { label: "vs Trello", href: "/vs/trello" },
    { label: "vs Asana", href: "/vs/asana" },
    { label: "vs Notion", href: "/vs/notion" },
    { label: "vs Monday.com", href: "/vs/monday" },
    { label: "vs ClickUp", href: "/vs/clickup" },
  ]},
  { title: "Company", links: [
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/operosmart/", external: true },
  ]},
  { title: "Legal", links: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
  ]},
];

export const SiteFooter = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      setSubscribed(true);
      toast.success("You're subscribed!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to subscribe"); }
    setSubscribing(false);
  };

  return (
    <footer className="border-t border-border bg-card" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-6 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <img src="/favicon.png" alt="Opero Smart" className="h-7 w-7 rounded-lg" />
            <span className="font-heading font-bold text-lg">Opero Smart</span>
          </div>
          <p className="text-sm text-muted-foreground mb-5 max-w-xs">Smart project management for teams that ship. Kanban, AI, chat and analytics in one clean workspace.</p>
          {subscribed ? (
            <p className="text-sm text-emerald-600 font-medium" data-testid="newsletter-success">✓ Thanks — you're on the list!</p>
          ) : (
            <form onSubmit={subscribe} className="flex gap-2 max-w-sm" data-testid="newsletter-form">
              <Input type="email" required placeholder="Your email for product updates" value={email} onChange={(e) => setEmail(e.target.value)} className="h-9 text-sm" data-testid="newsletter-email-input" />
              <Button type="submit" size="sm" className="h-9 shrink-0" disabled={subscribing} data-testid="newsletter-subscribe-btn">
                {subscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          )}
        </div>
        {COLUMNS.map(col => (
          <div key={col.title}>
            <h4 className="text-sm font-semibold mb-3">{col.title}</h4>
            <ul className="space-y-2">
              {col.links.map(l => (
                <li key={l.label}>
                  <a href={l.href} {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}>{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Opero Smart. All rights reserved.</span>
          <div className="flex items-center gap-2" data-testid="footer-creator-credit">
            <span>Created by</span>
            <img src="https://static.prod-images.emergentagent.com/jobs/488d4f94-f06c-450b-9416-83b7a331180f/images/9626ec3a252fef2f2334c5a5a11d99ec9e04b50d32c9c311c79da39e5a8e0d1d.png" alt="GH" className="h-5 w-auto" data-testid="gh-logo-badge" />
          </div>
        </div>
      </div>
    </footer>
  );
};
